import { Button, Popconfirm, Radio } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import React, { FC } from 'react';
import {
	AOPDecisionStep,
	AOPDraftStep,
	AOP_MAX_EXITS,
	AOP_MAX_QUESTION,
} from '../../../types/AOPTypes';
import { AOPSelect, AOPSelectOption, AOPTextArea } from '../fields/AOPFields';
import { AOPPanelRow, AOPPanelSection } from './AOPPanelShell';
import { AOPDurationField } from './AOPDurationField';
import {
	createEscalation,
	exitLabel,
	freeExitName,
	humaniseExitName,
	isCatchAllExit,
	renameStepPatch,
	stepNumberOf,
	stepTitle,
	withEscalations,
} from '../utils/AOPPlanUtils';

/**
 * What a decision asks, what its answers are, and where each one goes —
 * including the branch it takes when no answer arrives.
 *
 * Its own file, apart from the reminders it sends while it waits: the two
 * share only the step they are editing.
 */

/** Exit names are `^[a-z][a-z0-9_]*$` upstream, so an answer is slugified. */
const toExitName = (label: string): string => {
	const slug = label
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');
	return slug && /^[a-z]/.test(slug) ? slug.slice(0, 60) : `answer_${slug}`.slice(0, 60);
};

interface AOPDecisionQuestionProps {
	step: AOPDecisionStep;
	/** Every step in the plan, in order — what a branch can be pointed at. */
	steps: AOPDraftStep[];
	isReadOnly: boolean;
	onChange: (patch: Partial<AOPDecisionStep>) => void;
	/** Takes the author to the reminders, from the branch that mentions them. */
	onShowEscalations: () => void;
}

export const AOPDecisionQuestion: FC<AOPDecisionQuestionProps> = ({
	step,
	steps,
	isReadOnly,
	onChange,
	onShowEscalations,
}) => {
	const answers = step.exits.filter((exit) => !isCatchAllExit(exit.name));
	const catchAll = step.exits.filter((exit) => isCatchAllExit(exit.name))[0];
	const escalations = step.escalations ? step.escalations : [];

	/*
	  Which way the step gives up, read off the escalations rather than the
	  catch-all's name.

	  They say the same thing — the service ties the name to whether there are
	  escalations — but the escalations are there even when the exit is not. A
	  decision the compiler left with no exits at all used to leave this control
	  with nothing to show, so the one step that needed repairing was the one
	  that could not be.
	*/
	const mode: 'timeout' | 'escalation' = escalations.length > 0 ? 'escalation' : 'timeout';

	/**
	 * What the service will take as a decision's question: required, and 500
	 * characters at most.
	 */
	const validateQuestion = (question: string): string | null => {
		const trimmed = question.trim();

		if (trimmed.length === 0) {
			return 'A decision needs a question.';
		}

		if (trimmed.length > AOP_MAX_QUESTION) {
			return `${AOP_MAX_QUESTION} characters at most — this one is ${trimmed.length}.`;
		}

		return null;
	};

	/**
	 * What an answer may be called, given the exits this step already has.
	 *
	 * Two rules, both of which the service enforces by refusing the whole plan:
	 * no two exits may share a name, and the reserved names belong to the
	 * catch-all. The second is easy to reach by accident — wording an answer as
	 * "On exhausted" slugifies to one of them, and on a step currently using
	 * the other there is no duplicate to notice.
	 */
	const validateAnswer =
		(index: number) =>
		(label: string): string | null => {
			const name = toExitName(label);

			if (isCatchAllExit(name)) {
				return `“${exitLabel(
					name
				)}” is the name of this step's no-answer branch. Try another wording.`;
			}

			const clash = step.exits.filter(
				(other, position) => other.name === name && position !== index
			)[0];

			return clash
				? `“${exitLabel(clash.name)}” already uses that name. Try another wording.`
				: null;
		};

	/*
	  Where a new outcome points when there is no exit to copy from.

	  A decision with no exits at all is the state the service reports as
	  `missing_exit`, and Add outcome used to read `exits[0].target` and throw on
	  it — the one control that could repair the step crashed on the step that
	  needed repairing. The first other step is a target the author can see and
	  change; on a one-step plan there is nowhere else to point.
	*/
	const fallbackTarget = (() => {
		const other = steps.filter((entry) => entry.name !== step.name)[0];
		return other ? other.name : step.name;
	})();

	const targetOptions: AOPSelectOption[] = steps.map((other, index) => ({
		value: other.name,
		label: `${stepNumberOf(index)} · ${stepTitle(other)}`,
	}));

	/**
	 * Picks how the step gives up waiting.
	 *
	 * Escalation means having at least one, so choosing it makes the first —
	 * "escalation with none" is the invalid pairing the service refuses. Choosing
	 * Timeout drops them all, which is why that direction is confirmed.
	 * `withEscalations` renames the branch to match, and makes one if the step
	 * had none.
	 */
	const setMode = (next: 'timeout' | 'escalation') =>
		onChange(
			withEscalations(
				step,
				next === 'escalation' ? (escalations.length > 0 ? escalations : [createEscalation()]) : [],
				fallbackTarget
			)
		);

	const setCatchAllTarget = (target: string) => {
		if (!catchAll) {
			return;
		}

		onChange({
			exits: step.exits.map((exit) => (exit === catchAll ? { ...exit, target } : exit)),
		});
	};

	/** Renames an answer. What it may be called is `validateAnswer`'s business. */
	const renameAnswer = (index: number, label: string) => {
		const name = toExitName(label);

		onChange({
			exits: step.exits.map((exit, position) => (position === index ? { ...exit, name } : exit)),
		});
	};

	// The question itself is the panel's title — it names the block — so this tab
	// is what the question branches into, not the question again.
	const stepIds = steps.map((other) => other.name);

	const targetSelect = (exit: { name: string; target: string }, onPick: (next: string) => void) => {
		// An exit whose target names no step is what the service reports as
		// `unresolved`. The picker says so rather than showing an id that resolves
		// to nothing.
		const connected = stepIds.indexOf(exit.target) >= 0;

		return (
			<AOPSelect
				ariaLabel={`Where ${exitLabel(exit.name)} goes`}
				value={connected ? exit.target : ''}
				placeholder="Not connected"
				options={targetOptions}
				disabled={isReadOnly}
				onChange={onPick}
				className={connected ? '' : 'aop-select-invalid'}
			/>
		);
	};

	return (
		<>
			<AOPPanelSection title="Question" required first>
				<AOPTextArea
					ariaLabel="Question"
					rows={2}
					value={step.question}
					disabled={isReadOnly}
					// Said by the field rather than beside it: a question the service
					// refuses takes the whole plan patch down with it, and what comes
					// back names neither the field nor the rule.
					validate={validateQuestion}
					onCommit={(question) =>
						onChange(renameStepPatch(step, question.trim()) as Partial<AOPDecisionStep>)
					}
				/>
			</AOPPanelSection>

			<AOPPanelSection
				title="Outcomes"
				required
				// The distinction the names do not carry on their own: an answer is
				// something somebody said, the catch-all is nobody saying anything.
				info="No is an explicit decline. Timeout means no answer before the wait or escalation ends."
				action={
					isReadOnly || step.exits.length >= AOP_MAX_EXITS ? null : (
						<Button
							type="link"
							className="px-0"
							onClick={() =>
								onChange({
									exits: step.exits.concat([
										{
											// Counting the answers is not enough: one of them may
											// already have been renamed to the name that count
											// produces, and two exits sharing a name is rejected.
											name: freeExitName(step.exits),
											target: step.exits.length > 0 ? step.exits[0].target : fallbackTarget,
										},
									]),
								})
							}
						>
							+ Add outcome
						</Button>
					)
				}
			>
				{step.exits.map((exit, index) =>
					isCatchAllExit(exit.name) ? null : (
						<React.Fragment key={exit.name}>
							<AOPPanelRow label="If" required>
								<div className="flex items-start gap-xs">
									<div className="min-w-0 flex-1">
										<AOPTextArea
											ariaLabel="Outcome"
											rows={1}
											value={humaniseExitName(exit.name)}
											disabled={isReadOnly}
											validate={validateAnswer(index)}
											onCommit={(label) => renameAnswer(index, label)}
										/>
									</div>
									{isReadOnly || answers.length < 2 ? null : (
										<Popconfirm
											title="Remove this outcome?"
											okText="Remove"
											okButtonProps={{ danger: true }}
											cancelText="Cancel"
											onConfirm={() =>
												onChange({ exits: step.exits.filter((other) => other !== exit) })
											}
										>
											<Button
												type="text"
												aria-label={`Remove outcome ${exit.name}`}
												icon={<DeleteOutlined />}
											/>
										</Popconfirm>
									)}
								</div>
							</AOPPanelRow>

							<AOPPanelRow label="Go to" required>
								{targetSelect(exit, (target) =>
									onChange({
										exits: step.exits.map((other, position) =>
											position === index ? { ...other, target } : other
										),
									})
								)}
							</AOPPanelRow>
						</React.Fragment>
					)
				)}

				{catchAll ? (
					<>
						<AOPPanelRow label="If" required>
							<span className="bodymedium block pt-xs text-character-primary">No answer</span>
						</AOPPanelRow>

						<AOPPanelRow label="Go to" required>
							<Radio.Group className="flex flex-col gap-s pt-xs" disabled={isReadOnly} value={mode}>
								<div className="flex flex-col gap-2xs">
									{/*
									  Switching away from Escalation drops the escalation steps,
									  because a decision with escalations has to take the
									  `on_exhausted` branch. Confirmed first, like any removal.
									*/}
									<Popconfirm
										title="Remove the escalation steps? A Timeout branch cannot have any."
										okText="Remove"
										okButtonProps={{ danger: true }}
										cancelText="Cancel"
										disabled={isReadOnly || escalations.length === 0}
										onConfirm={() => setMode('timeout')}
									>
										<Radio
											value="timeout"
											onClick={() => {
												if (escalations.length === 0) {
													setMode('timeout');
												}
											}}
										>
											Timeout
										</Radio>
									</Popconfirm>
									<span className="footnoteregular pl-l text-character-secondary">
										Wait a set time, then follow the Timeout path.
									</span>

									{mode === 'timeout' ? (
										<div className="flex flex-col gap-2xs pl-l pt-xs">
											<span className="footnoteregular text-character-secondary">Wait for</span>
											<AOPDurationField
												ariaLabel="Wait for"
												seconds={step.timeout_seconds}
												disabled={isReadOnly}
												onCommit={(timeout_seconds) => onChange({ timeout_seconds })}
											/>
										</div>
									) : null}
								</div>

								<div className="flex flex-col gap-2xs">
									{/*
									  Choosing it makes the first escalation and opens the tab:
									  that tab is closed while the step has none, so this is the
									  only way in, and arriving on an empty one would say the
									  author had something to edit when they did not.
									*/}
									<Radio
										value="escalation"
										onClick={() => {
											if (escalations.length === 0) {
												setMode('escalation');
												onShowEscalations();
											}
										}}
									>
										Escalation
									</Radio>
									<span className="footnoteregular pl-l text-character-secondary">
										Follow up with the supplier in steps until there&rsquo;s an answer.
									</span>

									{mode === 'escalation' ? (
										// No duration here: with escalations the step gives up once
										// the initial wait and every escalation's window have run,
										// which is derived rather than set. The initial wait is set
										// on the Escalations tab, where the rest of the sequence is.
										<div className="flex flex-col gap-s pl-l">
											<Button
												type="link"
												className="self-start px-0"
												onClick={() => onShowEscalations()}
											>
												{escalations.length === 0
													? 'Create escalation steps'
													: 'Edit escalation steps'}
											</Button>
										</div>
									) : null}

									<div className="flex flex-col gap-2xs pl-l pt-xs">
										<span className="footnoteregular text-character-secondary">
											<span className="mr-2xs text-character-danger">*</span>
											{mode === 'escalation'
												? 'If no response after escalation, go to'
												: 'If no response, go to'}
										</span>
										{/* A step the compiler left with no exits has none to point
										    yet; the first edit makes one. */}
										{catchAll ? targetSelect(catchAll, setCatchAllTarget) : null}
									</div>
								</div>
							</Radio.Group>
						</AOPPanelRow>
					</>
				) : null}
			</AOPPanelSection>
		</>
	);
};
